import { useMediaQuery, useTheme } from '@mui/material';
import type {
  GridColumnVisibilityModel,
  GridRowParams,
  GridRowSelectionModel,
  GridSortModel,
} from '@mui/x-data-grid-pro';
import { useCallback, useEffect, useState, type Dispatch, type SetStateAction } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { ScrollableDataGrid } from '~/components/data-grid';
import { EmptyState } from '~/components/empty-state';
import { ListCheckbox } from '~/components/list-row';
import { ScheduledShowDialog } from '~/components/schedule/dialog';
import type { ScheduleInput } from '~/generated/graphql';
import { useLink } from '~/hooks/link';
import { useOrderByParams } from '~/hooks/order-by';
import AddScheduleIllustration from '~/images/illustrations/add-schedule.svg';
import type { ScheduledShows__ScheduledShowList as ScheduledShow } from '../../queries/queries.generated';
import { useColumns } from '../lib/columns';

const NoRowsOverlay = () => {
  const { t } = useTranslation();
  return (
    <EmptyState
      illustration={AddScheduleIllustration}
      header={t('pages.schedules.list.empty_state.header')}
      description={t('pages.schedules.list.empty_state.description')}
    />
  );
};
export interface TableProps {
  loading: boolean;
  onCheck: Dispatch<SetStateAction<GridRowSelectionModel>>;
  onDelete: (scheduledShow: ScheduledShow) => void;
  onSubmit: (
    name: string,
    schedule: ScheduleInput,
    showId: number,
    scheduledShowId?: number,
  ) => Promise<void>;
  scheduledShows: readonly ScheduledShow[];
  selectedIds: GridRowSelectionModel;
}

export const Table = ({
  loading,
  onCheck,
  onDelete,
  onSubmit,
  scheduledShows,
  selectedIds,
}: TableProps) => {
  const [showColumns, setShowColumns] = useState<GridColumnVisibilityModel>({});
  const navigate = useNavigate();
  const link = useLink();

  const theme = useTheme();
  const isSmallAndDown = useMediaQuery(theme.breakpoints.down('sm'));

  const { setOrderByParams, sortModel } = useOrderByParams();

  const onSortModelChange = useCallback(
    (sortModel: GridSortModel) => {
      setOrderByParams(sortModel.at(0) ?? null);
    },
    [setOrderByParams],
  );

  useEffect(() => {
    setShowColumns((x) => ({
      ...x,
      devices: !isSmallAndDown,
      label: !isSmallAndDown,
      schedule: !isSmallAndDown,
      start: !isSmallAndDown,
    }));
  }, [isSmallAndDown]);

  const [currentSchedule, setCurrentSchedule] = useState<ScheduledShow>();
  const [editDialogOpen, setEditDialogOpen] = useState(false);

  const onUpdate = (scheduledShow: ScheduledShow) => {
    setCurrentSchedule(scheduledShow);
    setEditDialogOpen(true);
  };

  const columns = useColumns({ onDelete, onUpdate });

  return (
    <>
      <ScrollableDataGrid
        checkboxSelection={!isSmallAndDown}
        columns={columns}
        columnVisibilityModel={showColumns}
        disableColumnFilter
        disableColumnMenu
        disableColumnSelector
        disableDensitySelector
        disableRowSelectionOnClick
        loading={loading}
        onColumnVisibilityModelChange={setShowColumns}
        onRowSelectionModelChange={onCheck}
        onRowClick={({ row }: GridRowParams<ScheduledShow>) =>
          navigate(link(`/schedules/${String(row.id)}/details`))
        }
        rows={scheduledShows}
        rowSelectionModel={selectedIds}
        slots={{
          baseCheckbox: ListCheckbox,
          columnResizeIcon: () => null,
          noRowsOverlay: NoRowsOverlay,
        }}
        sortingMode="server"
        sortModel={sortModel}
        onSortModelChange={onSortModelChange}
      />
      {currentSchedule && (
        <ScheduledShowDialog
          onClose={() => setEditDialogOpen(false)}
          onSubmit={onSubmit}
          open={editDialogOpen}
          scheduledShow={currentSchedule}
        />
      )}
    </>
  );
};
